import { NgModule, LOCALE_ID, DEFAULT_CURRENCY_CODE } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeEsCO from '@angular/common/locales/es-CO';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// Registrar locale es-CO para que DatePipe use formato DD/MM/YYYY
registerLocaleData(localeEsCO, 'es-CO');
import { HeaderComponent } from './shared/header/header.component';
import { FooterComponent } from './shared/footer/footer.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { DefaultLayoutComponent } from './layout/default-layout/default-layout.component';
import { HomeComponent } from './components/home/home.component';
import { ProjectsComponent } from './components/projects/projects.component';
import { NewProjectComponent } from './components/projects/new-project/new-project.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ParticipantsComponent } from './components/participants/participants.component';
import { NewParticipantComponent } from './components/participants/new-participant/new-participant.component';
import { ViewProjectComponent } from './components/projects/view-project/view-project.component';
import { ConfigBudgetsComponent } from './components/config/config-budgets/config-budgets.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { SharedModalComponent } from './components/shared-modal/shared-modal.component';
import { NewCategoryComponent } from './components/config/config-budgets/new-category/new-category.component';
import { NewSubcategoryComponent } from './components/config/config-budgets/new-subcategory/new-subcategory.component';
import { ToastComponent } from './components/toast/toast.component';
import { ThirdPartiesComponent } from './components/third-parties/third-parties.component';
import { NewThirdPartieComponent } from './components/third-parties/new-third-partie/new-third-partie.component';
import { WorkOrdersComponent } from './components/work-orders/work-orders.component';
import { NewWorkOrderComponent } from './components/work-orders/new-work-order/new-work-order.component';
import { ExpenseReceiptsComponent } from './components/expense-receipts/expense-receipts.component';
import { NewExpenseReceiptComponent } from './components/expense-receipts/new-expense-receipt/new-expense-receipt.component';
import { CountWorkOrderStatusPipePipe } from './pipes/count-work-order-status-pipe.pipe';
import { ViewWorkOrderComponent } from './components/work-orders/view-work-order/view-work-order.component';
import { ViewExpenseReceiptComponent } from './components/expense-receipts/view-expense-receipt/view-expense-receipt.component';
import { LoginComponent } from './components/login/login.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { RequiredLabelDirective } from './directives/required-label.directive';
import { PreviewProjectComponent } from './components/projects/preview-project/preview-project.component';
import { EnrollmentsComponent } from './components/enrollments/enrollments.component';
import { PortfolioMovementsListComponent } from './components/enrollments/portfolio-movements-list/portfolio-movements-list.component';
import { NewEnrollmentComponent } from './components/enrollments/new-enrollment/new-enrollment.component';
import { CompaniesComponent } from './components/companies/companies.component';
import { NewCompanyComponent } from './components/companies/new-company/new-company.component';
import { ViewEnrollmentComponent } from './components/enrollments/view-enrollment/view-enrollment.component';
import { ViewEnrollmentV2Component } from './components/enrollments/view-enrollment-v2/view-enrollment-v2.component';
import { ViewCorporateEnrollmentComponent } from './components/enrollments/view-corporate-enrollment/view-corporate-enrollment.component';
import { ViewCorporateEnrollmentV2Component } from './components/enrollments/view-corporate-enrollment-v2/view-corporate-enrollment-v2.component';
import { EnrollmentAuditTimelineComponent } from './components/enrollments/enrollment-audit-timeline/enrollment-audit-timeline.component';
import { IncomeReceiptsComponent } from './components/income-receipts/income-receipts.component';
import { NewIncomeReceiptComponent } from './components/income-receipts/new-income-receipt/new-income-receipt.component';
import { ViewIncomeReceiptComponent } from './components/income-receipts/view-income-receipt/view-income-receipt.component';
import { ProfileParticipantComponent } from './components/participants/profile-participant/profile-participant.component';
import { ProfileThirdPartyComponent } from './components/third-parties/profile-third-party/profile-third-party.component';
import { ProfileCompanyComponent } from './components/companies/profile-company/profile-company.component';
import { CountConciliatePaymentPlansPipe } from './pipes/count-conciliate-payment-plans.pipe';
import { NewProjectBudgetComponent } from './components/projects/project-budget/new-project-budget/new-project-budget.component';
import { EditProjectComponent } from './components/projects/edit-project/edit-project.component';
import { CurrencyFormatterDirective } from './directives/app-currency-formatter.directive';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { CardModule } from 'primeng/card';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { EditPaymentPlansComponent } from './components/enrollments/edit-payment-plans/edit-payment-plans.component';
import { PaginatorModule } from 'primeng/paginator';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { NgChartsModule } from 'ng2-charts';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faWallet,
  faMoneyBillWave,
  faUsers,
  faBuilding,
  faRefresh,
  faChartLine,
  faClipboardList,
  faUserTie,
  faUser,
  faCalendar,
  faClipboardCheck,
  faReceipt,
  faCoins,
  faClipboard
} from '@fortawesome/free-solid-svg-icons';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ReportsHubComponent } from './components/reports/reports-hub/reports-hub.component';
import { BanksReportsComponent } from './components/reports/banks-reports/banks-reports.component';
import { IncomeReceiptsReportsComponent } from './components/reports/income-receipts-reports/income-receipts-reports.component';
import { ExpenseReceiptsReportsComponent } from './components/reports/expense-receipts-reports/expense-receipts-reports.component';
import { ProjectBudgetExecutionReportsComponent } from './components/reports/project-budget-execution-reports/project-budget-execution-reports.component';
import { WorkOrdersReportsComponent } from './components/reports/work-orders-reports/work-orders-reports.component';
import { ProjectProgramDetailReportsComponent } from './components/reports/project-program-detail-reports/project-program-detail-reports.component';
import { RecentReportJobsComponent } from './components/reports/recent-report-jobs/recent-report-jobs.component';
import { BanksComponent } from './components/config/banks/banks.component';
import { NewBankComponent } from './components/config/banks/new-bank/new-bank.component';
import { RefundVouchersComponent } from './components/refund-vouchers/refund-vouchers.component';
import { ViewRefundVoucherComponent } from './components/refund-vouchers/view-refund-voucher/view-refund-voucher.component';
import { NewRefundComponent } from './components/refund-vouchers/new-refund/new-refund.component';
import { ExcessPaymentsComponent } from './components/excess-payments/excess-payments.component';
import { ViewExcessPaymentComponent } from './components/excess-payments/view-excess-payment/view-excess-payment.component';
import { EditCorporateCompanyPlansComponent } from './components/enrollments/edit-corporate-company-plans/edit-corporate-company-plans.component';
import { EditCorporateParticipantPlansComponent } from './components/enrollments/edit-corporate-participant-plans/edit-corporate-participant-plans.component';
import { PaymentPlanAuditComponent } from './components/payment-plan-audit/payment-plan-audit.component';
import { PaymentPlanAuditBulkFixComponent } from './components/payment-plan-audit-bulk-fix/payment-plan-audit-bulk-fix.component';
import { EnrollmentStatusAuditComponent } from './components/enrollment-status-audit/enrollment-status-audit.component';
import { EnrollmentAuditBulkFixComponent } from './components/enrollment-audit-bulk-fix/enrollment-audit-bulk-fix.component';
import { ProjectDeletionConfirmComponent } from './components/projects/project-deletion-confirm/project-deletion-confirm.component';
import { ProjectDeletionProgressComponent } from './components/projects/project-deletion-progress/project-deletion-progress.component';
import { SystemResetComponent } from './components/config/system-reset/system-reset.component';
import { PaymentMethodsComponent } from './components/config/payment-methods/payment-methods.component';
import { NewPaymentMethodComponent } from './components/config/payment-methods/new-payment-method/new-payment-method.component';
import { ExchangeRateConfigComponent } from './components/config/exchange-rate/exchange-rate-config.component';
import { ExchangeRateHistoryComponent } from './components/config/exchange-rate/exchange-rate-history.component';
import { FeeTypesComponent } from './components/config/fee-types/fee-types.component';
import { NewFeeTypeComponent } from './components/config/fee-types/new-fee-type/new-fee-type.component';
import { AuditLogsComponent } from './components/config/audit-logs/audit-logs.component';
import { EmailLogsComponent } from './components/config/email-logs/email-logs.component';
import { PaymentLinkManagerComponent } from './components/shared/payment-link-manager/payment-link-manager.component';
import { ConfirmationModalComponent } from './components/shared/confirmation-modal/confirmation-modal.component';
import { ExcessSummaryModalComponent } from './components/shared/excess-summary-modal/excess-summary-modal.component';

// Nuevos componentes para manejo de sesión y autenticación
import { SessionWarningModalComponent } from './components/session-warning-modal/session-warning-modal.component';
import { SessionManagerComponent } from './components/session-manager/session-manager.component';
import { ProfileComponent } from './components/profile/profile.component';

// 🆕 Imports para conciliaciones bancarias
import { BankReconciliationListComponent } from './components/bank-reconciliation/bank-reconciliation-list/bank-reconciliation-list.component';
import { NewBankReconciliationComponent } from './components/bank-reconciliation/new-bank-reconciliation/new-bank-reconciliation.component';
import { ReceiptConfigModalComponent } from './components/bank-reconciliation/receipt-config-modal/receipt-config-modal.component';
import { BatchConfigModalComponent } from './components/bank-reconciliation/batch-config-modal/batch-config-modal.component';

// Imports para solicitudes de webhook Bitrix
import { BitrixRequestsComponent } from './components/bitrix-requests/bitrix-requests.component';
import { ViewBitrixRequestComponent } from './components/bitrix-requests/view-bitrix-request/view-bitrix-request.component';

// Imports para solicitudes de webhook Wompi
import { WompiRequestsComponent } from './components/wompi-requests/wompi-requests.component';
import { ViewWompiRequestComponent } from './components/wompi-requests/view-wompi-request/view-wompi-request.component';

// 🆕 Imports para relay de webhooks Wompi
import { WompiRelayListComponent } from './components/config/wompi-relay/wompi-relay-list.component';
import { NewWompiRelayComponent } from './components/config/wompi-relay/new-wompi-relay/new-wompi-relay.component';
import { WompiRelayLogsComponent } from './components/config/wompi-relay/wompi-relay-logs/wompi-relay-logs.component';

// 🆕 Componentes para gestión de roles y permisos
import { RolesListComponent } from './components/config/roles-permissions/roles-list/roles-list.component';
import { RoleFormComponent } from './components/config/roles-permissions/role-form/role-form.component';
import { UsersListComponent } from './components/config/roles-permissions/users-list/users-list.component';

// 🆕 Directivas para permisos
import { HasPermissionDirective, HasRoleDirective } from './directives/has-permission.directive';

// 🆕 Componentes standalone para carga de reportes
import { DailyTaskBannerComponent } from './components/daily-task-banner/daily-task-banner.component';
import { ReportUploadComponent } from './components/report-upload/report-upload.component';
import { ConciliationConfigComponent } from './components/config/conciliation-config/conciliation-config.component';

// 🆕 Componentes para Hub de Configuración
import { ConfigHubComponent } from './components/config/config-hub/config-hub.component';
import { UploadedReportsComponent } from './components/config/uploaded-reports/uploaded-reports.component';
import { ConciliationLogsComponent } from './components/config/conciliation-logs/conciliation-logs.component';
import { ConciliationLogDetailComponent } from './components/config/conciliation-logs/conciliation-log-detail/conciliation-log-detail.component';
import { ApproversComponent } from './components/config/approvers/approvers.component';

// Interceptors
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { PortfolioReportsComponent } from './components/reports/portfolio-reports/portfolio-reports.component';
import { CreatedProjectsReportsComponent } from './components/reports/created-projects-reports/created-projects-reports.component';
import { RegisteredDataReportsComponent } from './components/reports/registered-data-reports/registered-data-reports.component';
import { ParticipantsByIncpReportsComponent } from './components/reports/participants-by-incp-reports/participants-by-incp-reports.component';
import { AuditInfoComponent } from './components/shared/audit-info/audit-info.component';
import { InstallmentStatsComponent } from './components/shared/installment-stats/installment-stats.component';

// 🆕 Componentes para gestión de cartera (Retiros, Reversiones, Aplazamientos)
import { WithdrawalWizardComponent } from './components/enrollments/withdrawal-wizard/withdrawal-wizard.component';
import { ReversalWizardComponent } from './components/enrollments/reversal-wizard/reversal-wizard.component';
import { DeferralWizardComponent } from './components/enrollments/deferral-wizard/deferral-wizard.component';
import { PunishedPortfolioReportComponent } from './components/reports/punished-portfolio-report/punished-portfolio-report.component';

// 🆕 Componentes para gestión de cartera CORPORATIVA
import { CorporateWithdrawalWizardComponent } from './components/enrollments/corporate-withdrawal-wizard/corporate-withdrawal-wizard.component';
import { CorporateReversalWizardComponent } from './components/enrollments/corporate-reversal-wizard/corporate-reversal-wizard.component';
import { CorporateDeferralWizardComponent } from './components/enrollments/corporate-deferral-wizard/corporate-deferral-wizard.component';
import { AddCorporateParticipantsComponent } from './components/enrollments/add-corporate-participants/add-corporate-participants.component';

// 🆕 Componentes para Notas Débito y Crédito
import { DebitNotesComponent } from './components/debit-notes/debit-notes.component';
import { NewDebitNoteComponent } from './components/debit-notes/new-debit-note/new-debit-note.component';
import { ViewDebitNoteComponent } from './components/debit-notes/view-debit-note/view-debit-note.component';
import { CreditNotesComponent } from './components/credit-notes/credit-notes.component';
import { NewCreditNoteComponent } from './components/credit-notes/new-credit-note/new-credit-note.component';
import { ViewCreditNoteComponent } from './components/credit-notes/view-credit-note/view-credit-note.component';
import { ClosedPeriodsListComponent } from './components/bank-closing/closed-periods-list/closed-periods-list.component';
import { BusinessIncomeReceiptsComponent } from './components/income-receipts/business-income-receipts/business-income-receipts.component';
import { PendingApprovalsComponent } from './components/income-receipts/pending-approvals/pending-approvals.component';

// 🆕 Componentes para Cesiones de Cupo
import { EnrollmentTransfersComponent } from './components/enrollment-transfers/enrollment-transfers.component';
import { NewTransferComponent } from './components/enrollment-transfers/new-transfer/new-transfer.component';

// 🆕 Componentes para visualización de Payment Links
import { PaymentLinksComponent } from './components/payment-links/payment-links.component';
import { ViewPaymentLinkComponent } from './components/payment-links/view-payment-link/view-payment-link.component';
import { WompiConsolidatedReportComponent } from './components/reports/wompi-consolidated-report/wompi-consolidated-report.component';
import { WompiPaymentsReportComponent } from './components/reports/wompi-payments-report/wompi-payments-report.component';
import { PaymentLinksAuditReportComponent } from './components/reports/payment-links-audit-report/payment-links-audit-report.component';
import { PaymentPlanHistoryComponent } from './components/enrollments/payment-plan-history/payment-plan-history.component';
import { ProjectFinancialStatementReportsComponent } from './components/reports/project-financial-statement-reports/project-financial-statement-reports.component';
import { ProjectEnrollmentsReportsComponent } from './components/reports/project-enrollments-reports/project-enrollments-reports.component';


@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    DefaultLayoutComponent,
    HomeComponent,
    ProjectsComponent,
    NewProjectComponent,
    ParticipantsComponent,
    NewParticipantComponent,
    ViewProjectComponent,
    ConfigBudgetsComponent,
    SharedModalComponent,
    NewCategoryComponent,
    NewSubcategoryComponent,
    ToastComponent,
    ThirdPartiesComponent,
    NewThirdPartieComponent,
    WorkOrdersComponent,
    NewWorkOrderComponent,
    ExpenseReceiptsComponent,
    NewExpenseReceiptComponent,
    CountWorkOrderStatusPipePipe,
    ViewWorkOrderComponent,
    ViewExpenseReceiptComponent,
    LoginComponent,
    LoadingSpinnerComponent,
    RequiredLabelDirective,
    PreviewProjectComponent,
    EnrollmentsComponent,
    PortfolioMovementsListComponent,
    NewEnrollmentComponent,
    CompaniesComponent,
    NewCompanyComponent,
    ViewEnrollmentComponent,
    ViewEnrollmentV2Component,
    ViewCorporateEnrollmentComponent,
    ViewCorporateEnrollmentV2Component,
    EnrollmentAuditTimelineComponent,
    IncomeReceiptsComponent,
    NewIncomeReceiptComponent,
    ViewIncomeReceiptComponent,
    ProfileParticipantComponent,
    ProfileThirdPartyComponent,
    ProfileCompanyComponent,
    CountConciliatePaymentPlansPipe,
    NewProjectBudgetComponent,
    EditProjectComponent,
    CurrencyFormatterDirective,
    EditPaymentPlansComponent,
    ReportsHubComponent,
    BanksReportsComponent,
    IncomeReceiptsReportsComponent,
    ExpenseReceiptsReportsComponent,
    ProjectBudgetExecutionReportsComponent,
    WorkOrdersReportsComponent,
    ProjectProgramDetailReportsComponent,
    RecentReportJobsComponent,
    BanksComponent,
    NewBankComponent,
    RefundVouchersComponent,
    NewRefundComponent,
    ViewRefundVoucherComponent,
    ExcessPaymentsComponent,
    ViewExcessPaymentComponent,
    EditCorporateCompanyPlansComponent,
    EditCorporateParticipantPlansComponent,
    PaymentPlanAuditComponent,
    PaymentPlanAuditBulkFixComponent,
    EnrollmentStatusAuditComponent,
    EnrollmentAuditBulkFixComponent,
    ProjectDeletionConfirmComponent,
    ProjectDeletionProgressComponent,
    SystemResetComponent,
    PaymentMethodsComponent,
    NewPaymentMethodComponent,
    ExchangeRateConfigComponent,
    ExchangeRateHistoryComponent,
    FeeTypesComponent,
    NewFeeTypeComponent,
    AuditLogsComponent,
    EmailLogsComponent,
    PaymentLinkManagerComponent,
    ConfirmationModalComponent,
    ExcessSummaryModalComponent,
    // Nuevos componentes para manejo de sesión
    SessionWarningModalComponent,
    SessionManagerComponent,
    ProfileComponent,
    // 🆕 Nuevos componentes para conciliaciones bancarias
    BankReconciliationListComponent,
    NewBankReconciliationComponent,
    ReceiptConfigModalComponent,
    BatchConfigModalComponent,
    PortfolioReportsComponent,
    CreatedProjectsReportsComponent,
    RegisteredDataReportsComponent,
    ParticipantsByIncpReportsComponent,
    AuditInfoComponent,
    InstallmentStatsComponent,
    // 🆕 Componentes para gestión de cartera (Retiros, Reversiones, Aplazamientos)
    WithdrawalWizardComponent,
    ReversalWizardComponent,
    DeferralWizardComponent,
    PunishedPortfolioReportComponent,
    // 🆕 Componentes para gestión de cartera CORPORATIVA
    CorporateWithdrawalWizardComponent,
    CorporateReversalWizardComponent,
    CorporateDeferralWizardComponent,
    AddCorporateParticipantsComponent,
    // Componentes para solicitudes de webhook Bitrix
    BitrixRequestsComponent,
    ViewBitrixRequestComponent,
    // Componentes para solicitudes de webhook Wompi
    WompiRequestsComponent,
    ViewWompiRequestComponent,
    // 🆕 Componentes para relay de webhooks Wompi
    WompiRelayListComponent,
    NewWompiRelayComponent,
    WompiRelayLogsComponent,
    // 🆕 Componentes para gestión de roles y permisos
    RolesListComponent,
    RoleFormComponent,
    UsersListComponent,
    // 🆕 Directivas para permisos
    HasPermissionDirective,
    HasRoleDirective,
    // 🆕 Componentes para Hub de Configuración
    ConfigHubComponent,
    UploadedReportsComponent,
    ApproversComponent,
    ConciliationLogsComponent,
    ConciliationLogDetailComponent,
    // 🆕 Componentes para Notas Débito y Crédito
    DebitNotesComponent,
    NewDebitNoteComponent,
    ViewDebitNoteComponent,
    CreditNotesComponent,
    NewCreditNoteComponent,
    ViewCreditNoteComponent,
    ClosedPeriodsListComponent,
    BusinessIncomeReceiptsComponent,
    PendingApprovalsComponent,
    // 🆕 Componentes para Cesiones de Cupo
    EnrollmentTransfersComponent,
    NewTransferComponent,
    // 🆕 Componentes para visualización de Payment Links
    PaymentLinksComponent,
    ViewPaymentLinkComponent,
    WompiConsolidatedReportComponent,
    WompiPaymentsReportComponent,
    PaymentLinksAuditReportComponent,
    PaymentPlanHistoryComponent,
    // R5: Estado Financiero del Proyecto
    ProjectFinancialStatementReportsComponent,
    ProjectEnrollmentsReportsComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    DragDropModule,
    DropdownModule,
    MultiSelectModule,
    CardModule,
    CalendarModule,
    InputNumberModule,
    CheckboxModule,
    NgChartsModule,
    NgApexchartsModule,
    PaginatorModule,
    DialogModule,
    ButtonModule,
    TableModule,
    // 🆕 Componentes standalone para carga de reportes
    DailyTaskBannerComponent,
    ReportUploadComponent,
    ConciliationConfigComponent
  ],
  providers: [
    // Locale es-CO para formatos de fecha DD/MM/YYYY en todo el sistema
    { provide: LOCALE_ID, useValue: 'es-CO' },
    // Moneda por defecto del CurrencyPipe → COP (peso colombiano)
    // así `{{ value | currency }}` sin parámetros muestra COP $ en vez de US$
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'COP' },
    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(
      faWallet,
      faMoneyBillWave,
      faUsers,
      faBuilding,
      faRefresh,
      faChartLine,
      faClipboardList,
      faUserTie,
      faUser,
      faCalendar,
      faClipboardCheck,
      faReceipt,
      faCoins,
      faClipboard
    );
  }
}
